import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { endPoints } from "../../utils/endPoints";
import checkAuth from "../../app/auth";

/**
 * Async Thunk
 */
export const fetchTeacherDashboard = createAsyncThunk(
  "teacherDashboard/fetchTeacherDashboard",
  async (
    {
      page,
      page_size,
      search = "",
      syllabus_id,
      class_id,
      subject_id,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const params = {
        page,
        search,
      };

      // 🟢 Only attach page_size if available
      if (page_size) {
        params.page_size = page_size;
      }

      if (syllabus_id) params.syllabus_id = syllabus_id;
      if (class_id) params.class_id = class_id;
      if (subject_id) params.subject_id = subject_id;

      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TEACHER_DASHBOARD}`,
        { params },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

// ===============================================
// GET NOTIFICATIONS
// ===============================================
export const getNotifications = createAsyncThunk(
  "notifications/getNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_NOTIFICATIONS}`,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch notifications",
      );
    }
  },
);

// ===============================================
// MARK NOTIFICATION AS READ
// ===============================================
export const markNotificationRead = createAsyncThunk(
  "notifications/markNotificationRead",
  async ({ notification_id, is_read = true }, { rejectWithValue }) => {
    try {
      const payload = {
        notification_id,
        is_read,
      };

      const res = await checkAuth.patch(
        `${process.env.REACT_APP_BASE_URL}${endPoints.MARK_NOTIFICATION_READ}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to mark notification as read",
      );
    }
  },
);

// ===============================================
// MARK ALL NOTIFICATIONS AS READ
// ===============================================
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllNotificationsRead",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { schoolTeacher } = getState();
      const notifications = schoolTeacher.notifications || [];

      // take only unread IDs
      const unreadIds = notifications
        .filter((n) => !n.is_read)
        .map((n) => n.id);

      if (unreadIds.length === 0) {
        return { ids: [], unread_count: 0 };
      }

      const payload = {
        notification_id: unreadIds,
        is_read: true,
      };

      const res = await checkAuth.patch(
        `${process.env.REACT_APP_BASE_URL}${endPoints.MARK_NOTIFICATION_READ}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      return { ids: unreadIds, unread_count: 0 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to mark all notifications as read",
      );
    }
  },
);

// GET QUESTION DETAIL BY ID
export const getQuestionDetailById = createAsyncThunk(
  "assessment/getQuestionDetailById",
  async ({ question_id }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_QUESTION_DETAIL_BY_ID}`,
        { question_id },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch question detail",
      );
    }
  },
);

// ===============================================
// UPDATE MARKS FOR ASSIGNMENT MAPPED QUESTION
// ===============================================
export const updateQuestionMarks = createAsyncThunk(
  "assignment/updateQuestionMarks",
  async ({ assignment_id, question_id, marks }, { rejectWithValue }) => {
    try {
      const payload = {
        assignment_id,
        question_id,
        marks,
      };

      const res = await checkAuth.patch(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TEACHER_UPDATE_MARKS}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update marks");
    }
  },
);

export const generateQuestionsByOCR = createAsyncThunk(
  "questionBank/generateQuestionsByOCR",
  async (
    {
      file,
      curriculum_id,
      class_id,
      subject_id,
      chapter_id,
      topic_id,
      difficulty_level,
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // 🔥 Append these 5 IDs
      formData.append("curriculum_id", curriculum_id);
      formData.append("class_id", class_id);
      formData.append("subject_id", subject_id);
      formData.append("chapter_id", chapter_id);
      formData.append("topic_id", topic_id);
      formData.append("difficulty_level", difficulty_level);

      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.EXTRACT_OCR_QUESTIONS}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to extract OCR questions",
      );
    }
  },
);

// ===============================================
// GENERATE QUESTIONS BY AI (POST FormData)
// ===============================================
export const generateQuestionsByAi = createAsyncThunk(
  "questionBank/generateQuestionsByAi",
  async (
    {
      curriculum_id,
      class_id,
      subject_id,
      chapter_id,
      topic_id,
      difficulty_level,
      question_type,
      question_count,
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();

      formData.append("curriculum_id", curriculum_id);
      formData.append("class_id", class_id);
      formData.append("subject_id", subject_id);

      if (chapter_id) formData.append("chapter_id", chapter_id);
      if (topic_id) formData.append("topic_id", topic_id);

      formData.append("difficulty_level", difficulty_level);
      formData.append("question_type", question_type);
      formData.append("question_count", question_count);

      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GENERATE_AI_QUESTIONS}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to generate AI questions",
      );
    }
  },
);

export const saveAiGeneratedQuestions = createAsyncThunk(
  "teacher/saveAiGeneratedQuestions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.AI_GENERATED_QUES_SAVE}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to save questions" },
      );
    }
  },
);

// DELETE QUESTION BY ID
export const deleteQuestionById = createAsyncThunk(
  "assessment/deleteQuestionById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.delete(
        `${process.env.REACT_APP_BASE_URL}${endPoints.DELETE_QUESTION_BANK}`,
        {
          data: {
            question_ids: [id], // ✅ must be inside data
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete question",
      );
    }
  },
);

export const createAndUpdateContentLibrary = createAsyncThunk(
  "teacherDashboard/createAndUpdateContentLibrary",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TEACHER_CONTENT_UPLOAD_WITH_UPDATE}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getTeacherContentLibrary = createAsyncThunk(
  "teacherDashboard/getTeacherContentLibrary",
  async (
    {
      page = 1,
      page_size = 10,

      class_id,
      school_id,
      syllabus_id,
      subject_id,

      section_id,
      chapter_id,
      topic_id,
      content_id,
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_TEACHER_CONTENT_LIBRARY}`,
        {
          params: {
            page,
            page_size,

            class_id,
            school_id,
            syllabus_id,
            subject_id,

            section_id,
            chapter_id,
            topic_id,
            content_id,
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response || "Failed to fetch content library",
      );
    }
  },
);

export const getTocData = createAsyncThunk(
  "teacherDashboard/getTocData",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("pdf_path", file);

      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TABLE_OF_CONTENT_DATA_EXTRATECT}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/* =========================================================
   ADD / UPDATE QUESTIONS
========================================================= */
export const addOrUpdateQuestions = createAsyncThunk(
  "teacher/addOrUpdateQuestions",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.CREATE_UPDATE_QUESTION_BANK}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * GET Question Bank
 */
export const getQuestionBank = createAsyncThunk(
  "questionBank/getQuestionBank",
  async (params, { rejectWithValue }) => {
    try {
      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_QUESTION_BANK_ALL_QUESTIONS}`,
        {
          params: {
            syllabus_id: params.syllabus_id,
            class_id: params.class_id,
            subject_id: params.subject_id,
            chapter_id: params.chapter_id || undefined,
            topic_id: params.topic_id || undefined,

            // 🔥 For Question Bank UI
            question_type: params.question_type || undefined,
            difficulty_level: params.difficulty_level || undefined,
            question_text: params.question_text || undefined,
            question_source: params.question_source || undefined,

            // 🔥 For ASSIGNMENTS
            question_types: params.question_types || undefined,
            difficulty_levels: params.difficulty_levels || undefined,
            // required for “already mapped” UI
            assignment_id: params.assignment_id || undefined,

            page: params.page || 1,
            page_size: params.page_size || 10,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch question bank",
      );
    }
  },
);

export const addTocData = createAsyncThunk(
  "teacherDashboard/addTocData",
  async (data, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.BULK_UPLOAD_TABLE_OF_CONTENT}`,
        data,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * Add or Update Assessment with Questions
 * POST to ASSESMENT_ADD_UPDATE_QUESTION endpoint
 * Handles both Create and Update based on presence of assessment_id
 */
export const addOrUpdateAssessmentWithQuestions = createAsyncThunk(
  "teacherDashboard/addOrUpdateAssignment",
  async (payload, { rejectWithValue }) => {
    try {
      /**
       * Payload aligned with backend AddAssignment schema
       */
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.ADD_UPDATE_ASSIGNMENT}`,
        payload,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * Map Questions to Assignment
 * POST to MAP_QUESTIONS_TO_ASSESMENT endpoint
 * Maps selected questions to an existing assignment
 */
export const mapQuestionsToAssignment = createAsyncThunk(
  "teacherDashboard/mapQuestionsToAssignment",
  async (payload, { rejectWithValue }) => {
    try {
      /**
       * Payload structure:
       * {
       *   assignment_id: integer,
       *   questions: [
       *     {
       *       question_id: required,
       *       order: optional,
       *       marks_override: optional
       *     }
       *   ]
       * }
       */
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.MAP_QUESTIONS_TO_ASSESMENT}`,
        payload,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const GET_ASSIGNMENTS_BY_TEACHER = createAsyncThunk(
  "teacher/getAssignments",
  async (params, { rejectWithValue }) => {
    try {
      // Build the query params object
      const queryParams = {
        teacher_id: params.teacher_id,
        page: params.page || 1,
        limit: params.limit || 10,
      };

      // Add optional filter params only if they exist
      if (params.syllabus_id) queryParams.syllabus_id = params.syllabus_id;
      if (params.class_id) queryParams.class_id = params.class_id;
      if (params.subject_id) queryParams.subject_id = params.subject_id;
      if (params.chapter_id) queryParams.chapter_id = params.chapter_id;
      if (params.topic_id) queryParams.topic_id = params.topic_id;
      if (params.assignment_type)
        queryParams.assignment_type = params.assignment_type;
      if (params.assignment_id)
        queryParams.assignment_id = params.assignment_id;

      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_ASSIGNMENTS_BY_TEACHER}`,
        {
          params: queryParams,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * Get Single Assignment by ID
 * Used for edit functionality
 * Requires both teacher_id and assignment_id
 */
export const GET_ASSIGNMENT_BY_ID = createAsyncThunk(
  "teacher/getAssignmentById",
  async ({ teacher_id, assignment_id }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_ASSIGNMENT_BY_ID}`,
        {
          params: { teacher_id, assignment_id },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * Delete Assignment
 * Only creator can delete & only if no student has attempted
 */
export const DELETE_ASSIGNMENT = createAsyncThunk(
  "teacher/deleteAssignment",
  async ({ teacher_id, assignment_id }, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.DELETE_ASSESMENT}`,
        {
          teacher_id,
          assignment_id,
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Export to Excel
export const exportToExcel = createAsyncThunk(
  "teacher/exportToExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.EXPORT_TO_EXCEL}`,
        {
          params,
          responseType: "blob",
        },
      );

      // --- DOWNLOAD HERE (inside thunk) ---
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "questions.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return true; // ✅ serializable value
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Assignment Questions Summary Export to Excel
export const exportAssignmentQuestionsToExcel = createAsyncThunk(
  "teacher/exportAssignmentQuestionsToExcel",
  async ({ assignment_id }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.ASSIGNMENT_REVIEW_EXPORT_QUESTION}`,
        {
          params: { assignment_id },
          responseType: "blob",
        },
      );
      // --- DOWNLOAD HERE (inside thunk) ---
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assignment_${assignment_id}_summary.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return true; // ✅ serializable value
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Assignment Students Summary Export to Excel
export const exportAssignmentStudentsToExcel = createAsyncThunk(
  "teacher/exportAssignmentStudentsToExcel",
  async ({ assignment_id }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.ASSIGNMENT_REVIEW_EXPORT_STUDENT}`,
        {
          params: { assignment_id },
          responseType: "blob",
        },
      );
      // --- DOWNLOAD HERE (inside thunk) ---
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assignment_${assignment_id}_students.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return true; // ✅ serializable value
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Assignment Summary Fetch
export const fetchAssignmentSummary = createAsyncThunk(
  "teacherDashboard/fetchAssignmentSummary",
  async (
    {
      assignment_id,
      view_type = "questions", // 🔥 default
      page = 1,
      page_size = 10,
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.ASSIGNMENT_SUMMARY}`,
        {
          params: {
            assignment_id,
            view_type, // 🔥 tells backend which list to paginate
            page,
            page_size,
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Assignment Summary Feedback (Teacher's feedback on student's answer for each question)
export const postAssignmentQuestionsFeedback = createAsyncThunk(
  "teacherDashboard/postAssignmentQuestionsFeedback",
  async ({ student_id, assignment_id, feedback_text }, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.ASSIGNMENT_TEACHER_FEEDBACK}`,
        {
          assignment_id,
          student_id,
          feedback_text,
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete Item from  Content Library (Teacher's uploaded content) - can be chapter, topic or content
export const deleteItem = createAsyncThunk(
  "deleteItem/delete",
  async ({ item_type, item_id, deletion_reason }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.delete(
        `${process.env.REACT_APP_BASE_URL}${endPoints.DELETE_UPLOAD_CONTENT}`,
        {
          data: {
            item_type,
            item_id,
            deletion_reason,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* =========================================
   RESET SUBJECT STRUCTURE API FOR CONTENT LIBRARY
========================================= */

export const resetSubjectStructure = createAsyncThunk(
  "content/resetSubjectStructure",
  async (data, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.RESET_STRUCTURE_CONTENT_LIBRARY}`,
        data,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const deleteContentFile = createAsyncThunk(
  "teacher/deleteContentFile",
  async (file_id, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.DELETE_UPLOAD_FILE}`,
        {
          file_id,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete content file",
      );
    }
  },
);

//Help Center API To fetch list of queries raised by students for assignments
export const fetchHelpCenterList = createAsyncThunk(
  "helpCenter/fetchHelpCenterList",
  async (
    {
      assignment_id,
      status = "", // default pending
      class_id,
      student_name,
      page = 1,
      page_size = 10,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_HELP_CENTER_QUERIES}?page=${page}&page_size=${page_size}`,
        {
          assignment_id,
          status,
          class_id,
          student_name,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

//Help Center API resolve help request
export const sendHelpResponse = createAsyncThunk(
  "help/sendResponse",
  async ({ id, response_message }, { rejectWithValue }) => {
    try {
      const response = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.RESOLVED_HELP_CENTER_QUERIES}`,
        {
          help_request_id: id,
          response_message,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to send help response",
      );
    }
  },
);

// teacher change password api
export const changePasswordTeacher = createAsyncThunk(
  "teacherDashboard/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TEACHER_CHANGE_PASSWORD}`,
        payload,
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * Slice
 */
const teacherDashboardSlice = createSlice({
  name: "teacherDashboard",
  initialState: {
    data: null,
    loading: false,
    error: null,
    notifications: [],
    unread_count: 0,
    notificationsLoading: false,
  },
  reducers: {
    clearTeacherDashboard: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET NOTIFICATIONS
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload?.notifications || []; // 👈 was ?.data
        state.unread_count = action.payload?.unread_count || 0; // 👈 add this
      })

      // MARK AS READ
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const updatedId = action.meta.arg.notification_id;

        state.notifications = state.notifications.map((notif) =>
          notif.id === updatedId ? { ...notif, is_read: true } : notif,
        );

        if (state.unread_count > 0) state.unread_count -= 1; // 👈 add this
      })

      .addCase(markAllNotificationsRead.fulfilled, (state, action) => {
        const ids = action.payload.ids;

        state.notifications = state.notifications.map((n) =>
          ids.includes(n.id) ? { ...n, is_read: true } : n,
        );

        state.unread_count = 0;
      })

      .addCase(generateQuestionsByAi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateQuestionsByAi.fulfilled, (state, action) => {
        state.loading = false;
        state.generatedAiQuestions = action.payload;
      })
      .addCase(generateQuestionsByAi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTeacherDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchTeacherDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addOrUpdateAssessmentWithQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        addOrUpdateAssessmentWithQuestions.fulfilled,
        (state, action) => {
          state.loading = false;
          state.data = action.payload;
        },
      )
      .addCase(addOrUpdateAssessmentWithQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(mapQuestionsToAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mapQuestionsToAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(mapQuestionsToAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearTeacherDashboard } = teacherDashboardSlice.actions;
export default teacherDashboardSlice.reducer;
